import React, { useState, useEffect } from 'react';
import moment from 'moment';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import DateRangePicker from '@/components/ui/date-range-picker';
import { Loader2, AlertCircle, Download } from 'lucide-react';
import { useReportData } from '../../../hooks/useReportData';
import { exportToCSV as exportCSV } from '../../../utils/exportToExcel';
import { formatExcelData } from '../../../utils/exportToExcel';
import { userService } from '../../../_services';

/**
 * Download Report Dialog
 * Modernized modal for downloading various admin reports
 * Supports: User Transactions by BIN, Groups with no Transactions,
 * Users Transactions, Parent Referral
 */
function DownloadReportDialog({ open, onOpenChange, title }) {
    // Date range state - Default to exactly 1 month back
    const defaultEndDate = moment().toDate();
    const defaultStartDate = moment().subtract(1, 'month').toDate();
    
    const [startDate, setStartDate] = useState(defaultStartDate);
    const [endDate, setEndDate] = useState(defaultEndDate);
    const [selectedBin, setSelectedBin] = useState('');
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState('');

    // Fetch BIN list if needed and dialog is open
    const needsBinList = title === "User Transactions by BIN" || title === "Groups with no Transactions";
    const { data: binData, loading: loadingBins } = useReportData(
        needsBinList ? 'binList' : null,
        {},
        needsBinList && open
    );

    // Set default BIN when data loads
    useEffect(() => {
        if (binData && binData.length > 0 && !selectedBin) {
            setSelectedBin(binData[0].bin);
        }
    }, [binData, selectedBin]);

    // Reset state when dialog closes
    useEffect(() => {
        if (!open) {
            setError('');
            setDownloading(false);
        } else {
            // Reset dates to default when dialog opens
            setStartDate(moment().subtract(1, 'month').toDate());
            setEndDate(moment().toDate());
        }
    }, [open]);

    // Handle date range change — onApply fires (event, picker) with moment objects
    const handleDateChange = (event, picker) => {
        setStartDate(picker.startDate.toDate());
        setEndDate(picker.endDate.toDate());
    };

    // Download report handler
    const handleDownload = async () => {
        setDownloading(true);
        setError('');

        try {
            const usermetaData = window.atob(localStorage.getItem('ajs_user_id'));
            const userId = usermetaData.split(':')[0];
            const userName = usermetaData.split(':')[1];
            const userRole = window.atob(localStorage.getItem('UserRole'));

            const FDate = moment(startDate).format("YYYY-MM-DD");
            const TDate = moment(endDate).format("YYYY-MM-DD");

            let response = null;
            let columnMap = {};
            let filename = '';

            switch (title) {
                case 'User Transactions by BIN':
                    if (!selectedBin) {
                        setError('Please select a BIN');
                        setDownloading(false);
                        return;
                    }
                    response = await userService.GetUserTransactionByBin(FDate, TDate, selectedBin, userId);
                    columnMap = {
                        distributorName: 'Distributor_Name',
                        emailId: 'Email_Id',
                        phoneNumber: 'Phone_Number',
                        createdOn: 'CreatedOn',
                        distributorCommission: 'Distributor_Commission',
                        bin: 'BIN',
                        compensable: 'Compensable',
                        firstTransactionDate: 'First_Transaction_Date'
                    };
                    filename = 'User_Transactions_by_BIN';
                    break;

                case 'Groups with no Transactions':
                    if (!selectedBin) {
                        setError('Please select a BIN');
                        setDownloading(false);
                        return;
                    }
                    response = await userService.GetGroupNoTransaction(FDate, TDate, selectedBin, userId);
                    columnMap = {
                        distributorName: 'DistributorName',
                        emailId: 'Email_Id',
                        phoneNumber: 'Phone_Number',
                        createdOn: 'CreatedOn',
                        groupNumber: 'GroupNumber'
                    };
                    filename = 'Groups_with_no_Transactions';
                    break;

                case 'Users Transactions':
                    response = await userService.GetTransaction(FDate, TDate);
                    columnMap = {
                        userName: 'Username',
                        fullName: 'Full Name',
                        email: 'Email ID',
                        phone: 'Phone',
                        paidClaimCount: 'Paid Claim Count',
                        firstTransactionDate: 'First Transaction Date',
                        address: 'Address'
                    };
                    filename = 'Users_Transactions';
                    break;

                case 'Parent Referral':
                    response = await userService.GetParentReferral(FDate, TDate);
                    columnMap = {
                        userName: 'Parent Distributor',
                        childName: 'Child Name',
                        childUserName: 'Child Username',
                        childEmail: 'Child Email',
                        childTelephone: 'Child Telephone',
                        childSignUpDate: 'Child Signup date',
                        transactionsTotal: 'Transactions total (All groups and all cards/BINs)'
                    };
                    filename = 'Parent_Referral_Report';
                    break;

                default:
                    throw new Error('Unknown report type');
            }

            if (!response || response.length === 0) {
                setError('No data available for the selected criteria');
                setDownloading(false);
                return;
            }

            // Transform data
            const transformRow = (row) => {
                const transformed = { ...row };
                
                // Format dates (remove time portion)
                if (transformed.createdOn) {
                    transformed.createdOn = transformed.createdOn.split('T')[0];
                }
                if (transformed.firstTransactionDate) {
                    transformed.firstTransactionDate = transformed.firstTransactionDate === null || transformed.firstTransactionDate === '' 
                        ? '-' 
                        : transformed.firstTransactionDate.split('T')[0];
                }
                if (transformed.childSignUpDate) {
                    transformed.childSignUpDate = transformed.childSignUpDate.split('T')[0];
                }
                
                // Remove commas from address
                if (transformed.address) {
                    transformed.address = transformed.address.split(',').join('');
                }
                
                return transformed;
            };

            const formattedData = formatExcelData(response, columnMap, transformRow);
            
            // Export to CSV
            exportCSV(formattedData, filename);

        } catch (err) {
            console.error('Error downloading report:', err);
            setError('Failed to download report. Please try again.');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle className="text-xl font-semibold text-primary">
                        {title} Report
                    </DialogTitle>
                    <DialogDescription>
                        Select criteria and download the report as CSV
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* BIN Selector (conditional) */}
                    {needsBinList && (
                        <div className="space-y-2">
                            <Label htmlFor="bin-select">BIN</Label>
                            {loadingBins ? (
                                <div className="flex items-center gap-2 text-primary/70">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    <span>Loading BINs...</span>
                                </div>
                            ) : binData && binData.length > 0 ? (
                                <Select value={selectedBin} onValueChange={setSelectedBin}>
                                    <SelectTrigger id="bin-select" className="w-full">
                                        <SelectValue placeholder="Select BIN" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {binData.map((item, index) => (
                                            <SelectItem key={index} value={item.bin}>
                                                {item.bin}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            ) : (
                                <Alert variant="destructive">
                                    <AlertCircle className="h-4 w-4" />
                                    <AlertDescription>
                                        No BINs available. Please contact administrator.
                                    </AlertDescription>
                                </Alert>
                            )}
                        </div>
                    )}

                    {/* Date Range Picker */}
                    <div className="space-y-2">
                        <Label>Date Range</Label>
                        <DateRangePicker
                            startDate={startDate}
                            endDate={endDate}
                            onApply={handleDateChange}
                            displayMode="unified"
                            compact
                        />
                    </div>

                    {/* Error Message */}
                    {error && (
                        <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={downloading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDownload}
                        disabled={downloading || loadingBins || (needsBinList && !selectedBin)}
                        variant="secondary"
                    >
                        {downloading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Downloading...
                            </>
                        ) : (
                            <>
                                <Download className="mr-2 h-4 w-4" />
                                Download
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default DownloadReportDialog;
